import { Link } from 'react-router-dom';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, Card, useMediaQuery, breakpoints,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import noCourseImg from '@src/assets/images/no-course-image.svg';

import type { CourseCardProps } from './types';
import messages from './messages';
import { getFullImageUrl, getStartDateDisplay } from './utils';

export const CourseCard = ({
  isLoading,
  courseId,
  courseName,
  courseImageUrl,
  courseStartDate,
  courseAdvertisedStart,
}: CourseCardProps) => {
  const intl = useIntl();
  const isExtraSmall = useMediaQuery({ maxWidth: breakpoints.small.maxWidth });

  const startDateDisplay = (courseStartDate || courseAdvertisedStart) ? getStartDateDisplay({
    start: courseStartDate,
    advertisedStart: courseAdvertisedStart,
  }, intl) : null;
  const hasStarted = Boolean(courseStartDate && new Date(courseStartDate).getTime() <= Date.now());
  const statusLabel = hasStarted
    ? intl.formatMessage(messages.current)
    : intl.formatMessage(messages.startingSoon);
  const metaLabel = hasStarted
    ? (startDateDisplay ? intl.formatMessage(messages.startedDate, { startDate: startDateDisplay }) : null)
    : intl.formatMessage(messages.openForEnrollment);

  return (
    <Card
      as={courseId ? Link : 'div'}
      to={courseId ? `/courses/${courseId}/about` : undefined}
      // TODO: Temporary use of `d-flex` to fix alignment. Remove once the related Paragon issue
      // (https://github.com/openedx/paragon/issues/3792) is resolved.
      className={`course-card d-flex ${isExtraSmall ? 'w-100' : 'course-card-desktop'}`}
      isClickable={!isLoading}
      isLoading={isLoading}
      data-testid="course-card"
    >
      <div className="course-card__media">
        <Card.ImageCap
          src={getFullImageUrl(courseImageUrl)}
          fallbackSrc={noCourseImg}
          srcAlt={courseName}
          skeletonDuringImageLoad
        />
        {!isLoading && (
          <div className="course-card__media-overlay">
            <Button variant="brand" className="course-card__learn-more-button">
              {intl.formatMessage(messages.learnMore)}
            </Button>
          </div>
        )}
      </div>
      <Card.Section className="course-card__body">
        <h3 className="course-card__title">{courseName}</h3>
        <p className="course-card__status">{statusLabel}</p>
        {metaLabel && (
          <p className="course-card__meta">
            <FontAwesomeIcon icon={faCalendarAlt} className="course-card__meta-icon" />
            <span>{metaLabel}</span>
          </p>
        )}
      </Card.Section>
    </Card>
  );
};
